import { useRef } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { OrbitControls } from '@react-three/drei';
import * as THREE from 'three';
import type { KonfiguratorState } from '../lib/konfigurator';
import { GLAS_OPTIONEN, RAHMEN_OPTIONEN } from '../lib/konfigurator';

interface Props { config: KonfiguratorState }

const BH = 0.08; // Brüstungshöhe 8 cm

function AluBox({ w, h, d, x, y, z, color }: { w:number; h:number; d:number; x:number; y:number; z:number; color:string }) {
  return (
    <mesh position={[x, y, z]}>
      <boxGeometry args={[w, h, d]} />
      <meshStandardMaterial color={color} metalness={0.7} roughness={0.3} />
    </mesh>
  );
}

function Panel({ x, glassColor, glassOpacity, aluColor, panelW, panelH }: {
  x: number; glassColor: string; glassOpacity: number; aluColor: string; panelW: number; panelH: number;
}) {
  const glassH = panelH - BH;
  return (
    <group position={[x, 0, 0]}>
      {/* Glas */}
      <mesh position={[0, BH / 2 + glassH / 2, 0]}>
        <boxGeometry args={[panelW - 0.004, glassH, 0.010]} />
        <meshPhysicalMaterial
          color={glassColor}
          transparent opacity={glassOpacity}
          roughness={0.05} metalness={0}
          transmission={glassOpacity < 0.4 ? 0.6 : 0}
        />
      </mesh>
      {/* Brüstungsprofil unten */}
      <mesh position={[0, BH / 2, 0]}>
        <boxGeometry args={[panelW - 0.004, BH, 0.022]} />
        <meshStandardMaterial color={aluColor} metalness={0.7} roughness={0.3} />
      </mesh>
    </group>
  );
}

function Pfeil({ dir, totalW, h }: { dir: 'links' | 'rechts'; totalW: number; h: number }) {
  const ref = useRef<THREE.Group>(null!);
  useFrame(({ clock }) => {
    const t = clock.getElapsedTime();
    const offset = ((Math.sin(t * 2) + 1) / 2) * 0.08;
    // Einwärts animieren: Pfeile bewegen sich zur Mitte
    ref.current.position.x = dir === 'links' ? -(totalW / 2 + 0.20 - offset) : (totalW / 2 + 0.20 - offset);
  });
  // Einwärts zeigen: links → Spitze nach rechts (+), rechts → Spitze nach links (-)
  const sign = dir === 'links' ? 1 : -1;
  return (
    <group ref={ref} position={[0, h * 0.5, 0.05]}>
      {/* Pfeilschaft */}
      <mesh position={[sign * 0.01, 0, 0]} rotation={[0, 0, Math.PI / 2]}>
        <cylinderGeometry args={[0.012, 0.012, 0.09, 12]} />
        <meshStandardMaterial color="#3b82f6" />
      </mesh>
      {/* Pfeilspitze */}
      <mesh position={[sign * 0.075, 0, 0]} rotation={[0, 0, dir === 'links' ? Math.PI / 2 : -Math.PI / 2]}>
        <coneGeometry args={[0.025, 0.05, 12]} />
        <meshStandardMaterial color="#3b82f6" />
      </mesh>
    </group>
  );
}

function AutoRotate({ enabled }: { enabled: boolean }): null {
  const t = useRef(0);
  useFrame((state, delta) => {
    if (!enabled) return;
    t.current += delta * 0.3;
    state.camera.position.x = Math.sin(t.current) * 3.5;
    state.camera.position.z = Math.cos(t.current) * 3.5;
    state.camera.lookAt(0, 0.5, 0);
  });
  return null;
}

function Szene({ config }: Props) {
  const glas   = GLAS_OPTIONEN.find(g => g.id === config.glasTyp)!;
  const rahmen = RAHMEN_OPTIONEN.find(r => r.id === config.rahmenFarbe)!;

  const W = config.breite / 1000;
  const H = config.hoehe  / 1000;
  const N = config.scheibenAnzahl;
  const aluColor = rahmen.hex;

  const postW  = 0.06;
  const railH  = 0.028;
  const innerW = W - postW * 2;
  const panelW = innerW / N;

  return (
    <>
      <ambientLight intensity={0.7} />
      <directionalLight position={[3, 5, 3]} intensity={1.2} castShadow />
      <directionalLight position={[-3, 2, -2]} intensity={0.5} />

      {/* obere Schiene */}
      <AluBox w={W} h={railH} d={0.05} x={0} y={H - railH/2} z={0} color={aluColor} />
      {/* untere Schiene */}
      <AluBox w={W} h={railH} d={0.05} x={0} y={railH/2} z={0} color={aluColor} />
      {/* linker Pfosten */}
      <AluBox w={postW} h={H} d={0.05} x={-(W/2 - postW/2)} y={H/2} z={0} color={aluColor} />
      {/* rechter Pfosten */}
      <AluBox w={postW} h={H} d={0.05} x={W/2 - postW/2} y={H/2} z={0} color={aluColor} />

      {/* Glasscheiben */}
      {Array.from({ length: N }).map((_, i) => {
        const xPos = -innerW / 2 + panelW * (i + 0.5);
        const zOff = i % 2 === 0 ? 0 : 0.014;
        return (
          <group key={i} position={[0, 0, zOff]}>
            <Panel
              x={xPos}
              glassColor={glas.farbe}
              glassOpacity={glas.opacity}
              aluColor={aluColor}
              panelW={panelW}
              panelH={H - railH * 2}
            />
          </group>
        );
      })}

      {/* Richtungspfeile */}
      {(config.oeffnungsRichtung === 'links' || config.oeffnungsRichtung === 'beidseitig') && (
        <Pfeil dir="links"  totalW={W} h={H} />
      )}
      {(config.oeffnungsRichtung === 'rechts' || config.oeffnungsRichtung === 'beidseitig') && (
        <Pfeil dir="rechts" totalW={W} h={H} />
      )}

      <AutoRotate enabled={false} />
      <OrbitControls enablePan={false} minDistance={1.5} maxDistance={8} target={[0, H / 2, 0]} />
    </>
  );
}

export default function GlasVorschau3D({ config }: Props) {
  return (
    <div className="w-full h-full rounded-xl overflow-hidden bg-gradient-to-b from-sky-50 to-slate-100">
      <Canvas
        camera={{ position: [0, 1.2, 3.5], fov: 45 }}
        shadows
        style={{ width: '100%', height: '100%' }}
      >
        <Szene config={config} />
      </Canvas>
    </div>
  );
}
